import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
const HomePage = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    
    const handleMouseMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };

    const heroSection = document.querySelector('.heroSection');
    if (heroSection) {
      heroSection.addEventListener('mousemove', handleMouseMove);
      return () => heroSection.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  const styles = `
    .homePage {
      width: 100%;
      overflow-x: hidden;
    }

    /* Hero Section with Animations */
    .heroSection {
      position: relative;
      background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
      min-height: 100vh;
      display: flex;
      align-items: center;
      overflow: hidden;
      cursor: none;
    }

    .heroBackground {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1;
    }

    .circuitPattern {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: 
        radial-gradient(circle at 20% 80%, rgba(0, 255, 255, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(0, 255, 255, 0.1) 0%, transparent 50%);
      animation: pulseGlow 4s ease-in-out infinite alternate;
    }

    .glowingCircles {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: 
        radial-gradient(circle at 30% 40%, rgba(0, 255, 255, 0.05) 0%, transparent 70%),
        radial-gradient(circle at 70% 70%, rgba(0, 122, 204, 0.05) 0%, transparent 70%);
      animation: floatingOrbs 6s ease-in-out infinite;
    }

    .techElements {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: 
        linear-gradient(45deg, transparent 48%, rgba(0, 255, 255, 0.1) 49%, rgba(0, 255, 255, 0.1) 51%, transparent 52%),
        linear-gradient(-45deg, transparent 48%, rgba(0, 255, 255, 0.05) 49%, rgba(0, 255, 255, 0.05) 51%, transparent 52%);
      background-size: 100px 100px;
      animation: gridMove 8s linear infinite;
    }

    .mouseFollower {
      position: absolute;
      width: 200px;
      height: 200px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(0, 255, 255, 0.1) 0%, transparent 70%);
      pointer-events: none;
      transform: translate(-50%, -50%);
      transition: all 0.1s ease;
      z-index: 2;
    }

    .heroContainer {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 60px;
      align-items: center;
      position: relative;
      z-index: 3;
    }

    .heroContent {
      color: white;
      opacity: ${isLoaded ? 1 : 0};
      transform: translateX(${isLoaded ? 0 : -50}px);
      transition: all 1s ease 0.3s;
    }

    .heroTitle {
      font-size: clamp(28px, 5vw, 52px);
      font-weight: 700;
      line-height: 1.2;
      margin-bottom: 24px;
      background: linear-gradient(135deg, #ffffff 0%, #00ffff 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      position: relative;
      animation: titleGlow 3s ease-in-out infinite alternate;
    }

    .heroTitle::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 0;
      height: 3px;
      background: linear-gradient(90deg, #00ffff, #ffffff);
      animation: underlineGrow 2s ease 1s forwards;
    }

    .heroSubtitle {
      font-size: clamp(14px, 2vw, 18px);
      line-height: 1.6;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 35px;
      opacity: 0;
      animation: fadeInUp 1s ease 1.5s forwards;
    }

    .ctaButton {
      background: linear-gradient(135deg, #00ffff 0%, #007acc 100%);
      color: white;
      padding: 15px 35px;
      border: none;
      border-radius: 50px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: all 0.3s ease;
      opacity: 0;
      animation: fadeInUp 1s ease 2s forwards;
      box-shadow: 0 5px 20px rgba(0, 255, 255, 0.3);
    }

    .ctaButton:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 30px rgba(0, 255, 255, 0.4);
    }

    .ctaButton::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      transition: left 0.5s;
    }

    .ctaButton:hover::before {
      left: 100%;
    }

    /* Enhanced Hero Visual */
    .heroVisual {
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      opacity: ${isLoaded ? 1 : 0};
      transform: translateX(${isLoaded ? 0 : 50}px);
      transition: all 1s ease 0.5s;
    }

    .techCircle {
      position: relative;
      width: clamp(300px, 35vw, 450px);
      height: clamp(300px, 35vw, 450px);
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .innerCore {
      width: clamp(80px, 10vw, 100px);
      height: clamp(80px, 10vw, 100px);
      background: radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(0, 255, 255, 0.7) 100%);
      border-radius: 50%;
      position: absolute;
      z-index: 4;
      animation: coreGlow 2s ease-in-out infinite alternate;
      box-shadow: 
        0 0 20px rgba(0, 255, 255, 0.5),
        0 0 40px rgba(0, 255, 255, 0.3),
        0 0 60px rgba(0, 255, 255, 0.1);
    }

    .orbitRing {
      position: absolute;
      border: 2px solid rgba(0, 255, 255, 0.3);
      border-radius: 50%;
      animation: rotateOrbit 10s linear infinite;
    }

    .orbitRing:nth-child(1) {
      width: 150px;
      height: 150px;
      animation-duration: 8s;
    }

    .orbitRing:nth-child(2) {
      width: 220px;
      height: 220px;
      animation-duration: 12s;
      animation-direction: reverse;
    }

    .orbitRing:nth-child(3) {
      width: 300px;
      height: 300px;
      animation-duration: 15s;
    }

    .orbitDot {
      position: absolute;
      width: 8px;
      height: 8px;
      background: #00ffff;
      border-radius: 50%;
      top: -4px;
      left: 50%;
      transform: translateX(-50%);
      box-shadow: 0 0 10px #00ffff;
    }

    .floatingParticles {
      position: absolute;
      width: 100%;
      height: 100%;
      pointer-events: none;
    }

    .particle {
      position: absolute;
      width: 4px;
      height: 4px;
      background: rgba(0, 255, 255, 0.6);
      border-radius: 50%;
      animation: floatParticle 6s linear infinite;
    }

    .particle:nth-child(1) { left: 10%; animation-delay: 0s; }
    .particle:nth-child(2) { left: 30%; animation-delay: 1s; }
    .particle:nth-child(3) { left: 50%; animation-delay: 2s; }
    .particle:nth-child(4) { left: 70%; animation-delay: 3s; }
    .particle:nth-child(5) { left: 90%; animation-delay: 4s; }

    /* Animations */
    @keyframes pulseGlow {
      0% { opacity: 0.3; }
      100% { opacity: 0.7; }
    }

    @keyframes floatingOrbs {
      0%, 100% { transform: translateY(0px) rotate(0deg); }
      50% { transform: translateY(-20px) rotate(180deg); }
    }

    @keyframes gridMove {
      0% { background-position: 0 0, 0 0; }
      100% { background-position: 100px 100px, -100px -100px; }
    }

    @keyframes titleGlow {
      0% { filter: drop-shadow(0 0 5px rgba(0, 255, 255, 0.3)); }
      100% { filter: drop-shadow(0 0 20px rgba(0, 255, 255, 0.6)); }
    }

    @keyframes underlineGrow {
      0% { width: 0; }
      100% { width: 60px; }
    }

    @keyframes fadeInUp {
      0% { opacity: 0; transform: translateY(30px); }
      100% { opacity: 1; transform: translateY(0); }
    }

    @keyframes coreGlow {
      0% { 
        transform: scale(1);
        box-shadow: 
          0 0 20px rgba(0, 255, 255, 0.5),
          0 0 40px rgba(0, 255, 255, 0.3);
      }
      100% { 
        transform: scale(1.1);
        box-shadow: 
          0 0 30px rgba(0, 255, 255, 0.7),
          0 0 60px rgba(0, 255, 255, 0.4),
          0 0 90px rgba(0, 255, 255, 0.2);
      }
    }

    @keyframes rotateOrbit {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @keyframes floatParticle {
      0% {
        transform: translateY(100vh) scale(0);
        opacity: 0;
      }
      10% {
        opacity: 1;
      }
      90% {
        opacity: 1;
      }
      100% {
        transform: translateY(-100px) scale(1);
        opacity: 0;
      }
    }

    /* Solutions Section - */
    .solutionsSection {
      padding: clamp(40px, 8vw, 80px) 0;
      background: #f8f9fa;
    }

    .sectionTitle {
      text-align: center;
      font-size: clamp(24px, 4vw, 32px);
      font-weight: 700;
      color: #333;
      margin-bottom: clamp(30px, 6vw, 60px);
      letter-spacing: 1px;
      padding: 0 20px;
    }

    .solutionsGrid {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(40px, 1fr));
      gap: clamp(20px, 3vw, 30px);
    }

    .solutionCard {
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }

    .solutionCard:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    }

    .cardImage {
      position: relative;
      width: 100%;
      aspect-ratio: 4 / 3;
      overflow: hidden;
      border-radius: 12px 12px 0 0;
      background: linear-gradient(45deg, #f0f0f0, #e0e0e0);
    }

    .solutionImage {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .imageOverlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: clamp(36px, 5vw, 48px);
      font-weight: bold;
    }

    .cardNumber {
      opacity: 0.8;
    }

    .cardTitle {
      padding: clamp(15px, 3vw, 20px);
      font-size: clamp(14px, 2vw, 16px);
      font-weight: 600;
      color: #333;
      text-align: center;
      line-height: 1.4;
      min-height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* About Section - */
    .aboutSection {
      padding: clamp(40px, 8vw, 80px) 0;
      background: white;
    }

    .aboutContainer {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: clamp(30px, 6vw, 60px);
      align-items: center;
    }

    .aboutVisual {
      display: flex;
      justify-content: center;
      align-items: center;
      order: 1;
    }

    .aboutIllustration {
      width: 100%;
      max-width: 400px;
      height: clamp(250px, 30vw, 300px);
      background: linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%);
      border-radius: 15px;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
    }

    .aboutImage {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 15px;
    }

    .aboutContent {
      order: 2;
    }

    .aboutTitle {
      font-size: clamp(28px, 4vw, 36px);
      font-weight: 700;
      color: #333;
      margin-bottom: clamp(20px, 3vw, 30px);
    }

    .aboutText {
      font-size: clamp(14px, 2vw, 16px);
      line-height: 1.6;
      color: #666;
      margin-bottom: clamp(15px, 2vw, 20px);
    }

    /* Why Choose Section - */
    .whyChooseSection {
      padding: clamp(40px, 8vw, 80px) 0;
      background: #f8f9fa;
    }

    .whyChooseContainer {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: clamp(30px, 6vw, 60px);
      align-items: center;
    }

    .whyChooseContent {
      order: 1;
    }

    .sectionLabel {
      color: #007acc;
      font-size: clamp(12px, 1.5vw, 14px);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 15px;
    }

    .whyChooseTitle {
      font-size: clamp(28px, 4vw, 36px);
      font-weight: 700;
      color: #333;
      margin-bottom: clamp(20px, 3vw, 30px);
      line-height: 1.3;
    }

    .redText {
      color: #dc3545;
    }

    .whyChooseText {
      font-size: clamp(14px, 2vw, 16px);
      line-height: 1.6;
      color: #666;
      margin-bottom: clamp(15px, 2vw, 20px);
    }

    .whyChooseVisual {
      display: flex;
      justify-content: center;
      align-items: center;
      order: 2;
    }

    .securityVisualization {
      width: clamp(250px, 30vw, 350px);
      height: clamp(250px, 30vw, 350px);
      background: linear-gradient(135deg, #007acc 0%, #00ffff 100%);
      border-radius: 50%;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
    }

    .securityVisualization::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 80%;
      height: 80%;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-radius: 50%;
    }

    .securityImage {
      width: 90%;
      height: 90%;
      object-fit: cover;
      border-radius: 50%;
    }

    /* Services Section - */
    .servicesSection {
      padding: clamp(60px, 10vw, 100px) 0;
      background: #f8f9fa;
    }

    .servicesGrid {
      max-width: 1000px;
      margin: 0 auto;
      padding: 0 20px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: clamp(30px, 4vw, 40px);
    }

    .serviceCard {
      position: relative;
      background: white;
      border-radius: 16px;
      padding: clamp(40px, 5vw, 50px);
      text-align: center;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
      overflow: hidden;
      border: 2px solid transparent;
      min-height: clamp(280px, 35vw, 320px);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    .serviceCard::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(0, 0, 0, 0.02),
        transparent
      );
      transition: left 0.6s ease;
    }

    .serviceCard:hover::before {
      left: 100%;
    }

    .serviceCard:hover {
      transform: translateY(-8px);
      box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
      border-color: #e9ecef;
    }

    .serviceCard:active {
      transform: translateY(-4px);
      transition: transform 0.1s ease;
    }

    .serviceIcon {
      width: clamp(60px, 8vw, 80px);
      height: clamp(60px, 8vw, 80px);
      margin: 0 auto clamp(20px, 3vw, 30px);
      background: #f8f9fa;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
    }

    .serviceIcon::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0;
      height: 0;
      background: #007acc;
      border-radius: 50%;
      transition: all 0.4s ease;
    }

    .serviceCard:hover .serviceIcon::before {
      width: 100%;
      height: 100%;
    }

    .serviceIcon svg {
      width: clamp(24px, 3vw, 32px);
      height: clamp(24px, 3vw, 32px);
      color: #6c757d;
      transition: all 0.3s ease;
      position: relative;
      z-index: 2;
    }

    .serviceCard:hover .serviceIcon svg {
      color: white;
      transform: scale(1.1);
    }

    .serviceTitle {
      font-size: clamp(18px, 2.5vw, 24px);
      font-weight: 700;
      color: #212529;
      margin-bottom: clamp(15px, 2vw, 20px);
      line-height: 1.3;
    }

    .serviceDescription {
      font-size: clamp(14px, 1.8vw, 16px);
      color: #6c757d;
      line-height: 1.6;
      margin-bottom: clamp(25px, 3vw, 30px);
    }

    .serviceLink {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #007acc;
      font-weight: 600;
      font-size: clamp(14px, 1.8vw, 16px);
      text-decoration: none;
      transition: all 0.3s ease;
      position: relative;
    }

    .serviceLink::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 2px;
      background: #007acc;
      transition: width 0.3s ease;
    }

    .serviceCard:hover .serviceLink::after {
      width: 100%;
    }

    .serviceLink svg {
      width: 16px;
      height: 16px;
      transition: transform 0.3s ease;
    }

    .serviceCard:hover .serviceLink svg {
      transform: translateX(4px);
    }

    /* Utility Classes */
    .desktopBr {
      display: block;
    }

    /* Responsive Design */
    @media (max-width: 1024px) {
      .heroContainer {
        grid-template-columns: 1fr;
        gap: 50px;
        text-align: center;
      }
      
      .heroContent {
        order: 2;
      }
      
      .heroVisual {
        order: 1;
      }
      
      .solutionsGrid {
        grid-template-columns: repeat(2, 1fr);
      }
      
      .aboutContainer {
        grid-template-columns: 1fr;
        text-align: center;
      }
      
      .aboutVisual {
        order: 1;
      }
      
      .aboutContent {
        order: 2;
      }
      
      .whyChooseContainer {
        grid-template-columns: 1fr;
        text-align: center;
      }
      
      .whyChooseContent {
        order: 2;
      }
      
      .whyChooseVisual {
        order: 1;
      }
      
      .servicesGrid {
        grid-template-columns: 1fr;
        gap: clamp(20px, 3vw, 30px);
      }
    }

    @media (max-width: 768px) {
      .heroSection {
        min-height: 100vh;
        padding: 20px 0;
      }
      
      .heroContainer {
        padding: 0 15px;
        gap: 40px;
      }
      
      .desktopBr {
        display: none;
      }
      
      .solutionsGrid {
        grid-template-columns: 1fr;
        padding: 0 15px;
      }
      
      .aboutContainer,
      .whyChooseContainer {
        padding: 0 15px;
      }
      
      .servicesGrid {
        grid-template-columns: 1fr;
        gap: clamp(20px, 3vw, 30px);
      }

      .techCircle {
        width: clamp(250px, 80vw, 350px);
        height: clamp(250px, 80vw, 350px);
      }
    }

    @media (max-width: 480px) {
      .heroSection {
        min-height: 90vh;
        padding: 15px 0;
      }
      
      .heroContainer {
        padding: 0 15px;
        gap: 30px;
      }

      .heroTitle {
        font-size: clamp(24px, 7vw, 32px);
      }

      .heroSubtitle {
        font-size: clamp(12px, 3vw, 16px);
      }

      .ctaButton {
        padding: 12px 28px;
        font-size: 14px;
      }
      
      .sectionTitle {
        font-size: clamp(20px, 4vw, 32px);
      }

      .whyChooseTitle, .aboutTitle {
        font-size: clamp(22px, 4vw, 36px);
      }
      
      .solutionsSection,
      .aboutSection,
      .whyChooseSection,
      .servicesSection {
        padding: 30px 0;
      }
      
      .solutionsGrid,
      .aboutContainer,
      .whyChooseContainer,
      .servicesGrid {
        padding: 0 15px;
      }
      
      .solutionCard {
        border-radius: 8px;
      }
      
      .aboutIllustration,
      .securityVisualization {
        width: 90%;
        height: auto;
        aspect-ratio: 1;
      }
      
      .servicesGrid {
        gap: clamp(15px, 3vw, 25px);
      }

      .techCircle {
        width: clamp(200px, 70vw, 280px);
        height: clamp(200px, 70vw, 280px);
      }

      .orbitRing:nth-child(1) { width: 120px; height: 120px; }
      .orbitRing:nth-child(2) { width: 180px; height: 180px; }
      .orbitRing:nth-child(3) { width: 240px; height: 240px; }
    }

    /* Enhanced hover effects for better interaction */
    @media (hover: hover) {
      .solutionCard:hover {
        transform: translateY(-8px);
      }
      
      .serviceCard:hover {
        transform: translateY(-8px);
      }
    }

    /* Focus states for accessibility */
    .solutionCard:focus-within,
    .serviceCard:focus-within,
    .ctaButton:focus {
      outline: 2px solid #007acc;
      outline-offset: 2px;
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="homePage">
        {/* Enhanced Hero Section */}
        <section className="heroSection">
          <div className="heroBackground">
            <div className="circuitPattern"></div>
            <div className="glowingCircles"></div>
            <div className="techElements"></div>
            <div 
              className="mouseFollower"
              style={{
                left: mousePosition.x,
                top: mousePosition.y
              }}
            ></div>
          </div>
          <div className="heroContainer">
            <div className="heroContent">
              <h1 className="heroTitle">
                Fast & Effective<br />
                Solutions with Virtual IP
              </h1>
              <p className="heroSubtitle">
                Expert Cyber Security Services with 24*7 support and significant solutions
                to make you feel safe in your workspace.
              </p>
              <Link to="/services/cyber-services">
              <button className="ctaButton">
                Get Started Today
              </button>
              </Link>
            </div>
            <div className="heroVisual">
              <div className="techCircle">
                <div className="innerCore"></div>
                <div className="orbitRing">
                  <div className="orbitDot"></div>
                </div>
                <div className="orbitRing">
                  <div className="orbitDot"></div>
                </div>
                <div className="orbitRing">
                  <div className="orbitDot"></div>
                </div>
                <div className="floatingParticles">
                  <div className="particle"></div>
                  <div className="particle"></div>
                  <div className="particle"></div>
                  <div className="particle"></div>
                  <div className="particle"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Professional Security Solutions */}
        

<section className="solutionsSection">
  <h2 className="sectionTitle">WE OFFER PROFESSIONAL SECURITY SOLUTIONS</h2>
  <div className="solutionsGrid">

    <Link to="/compliance/iso-27001" className="solutionCard">
      <div className="cardImage">
        <div className="imageOverlay">
          <span className="cardNumber">01</span>
        </div>
        <img src="/homepage1.png" alt="ISO 27001" className="solutionImage" />
      </div>
      <h3 className="cardTitle">ISO 27001 Certification Assistance</h3>
    </Link>

    <Link to="/compliance/soc2" className="solutionCard">
      <div className="cardImage">
        <div className="imageOverlay">
          <span className="cardNumber">02</span>
        </div>
        <img src="/homepage2.png" alt="SOC 2 Compliance" className="solutionImage" />
      </div>
      <h3 className="cardTitle">SOC 2 Compliance Support</h3>
    </Link>

    <Link to="/compliance/pci-dss" className="solutionCard">
      <div className="cardImage">
        <div className="imageOverlay">
          <span className="cardNumber">03</span>
        </div>
        <img src="/homepage3.png" alt="PCI-DSS Compliance" className="solutionImage" />
      </div>
      <h3 className="cardTitle">PCI-DSS Compliance Services</h3>
    </Link>

    <Link to="/compliance/gdpr" className="solutionCard">
      <div className="cardImage">
        <div className="imageOverlay">
          <span className="cardNumber">04</span>
        </div>
        <img src="/homepage4.png" alt="GDPR Compliance" className="solutionImage" />
      </div>
      <h3 className="cardTitle">GDPR Compliance Services</h3>
    </Link>

  </div>
</section>


        {/* Who We Are Section */}
        <section className="aboutSection">
          <div className="aboutContainer">
            <div className="aboutVisual">
              <div className="aboutIllustration">
                <img src="homepage5.png" alt="About Us" className="aboutImage"/>
              </div>
            </div>
            <div className="aboutContent">
              <h2 className="aboutTitle">Who We Are</h2>
              <p className="aboutText">
                At Virtual IP, we understand that transformation is not always easy. We've been 
                helping companies of all sizes respond to industry transitions in order to stay 
                competitive. Our years of experience have taught us to always make your business 
                success our priority.
              </p>
              <p className="aboutText">
                Our team of experts is ready to help you develop strategies for not only surviving, but 
                thriving in the future.
              </p>
              <p className="aboutText">
                Give us a call today to set up your first consultation.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="whyChooseSection">
          <div className="whyChooseContainer">
            <div className="whyChooseContent">
              <div className="sectionLabel">Why Choose Us</div>
              <h2 className="whyChooseTitle">
                Because We Bring Assurance To
                <span className="redText"> Your Business</span>
              </h2>
              <p className="whyChooseText">
                Cybersecurity is a complex field that requires a trusted partner capable of steering 
                the integration of people, processes, and technology. Businesses today need more than 
                just a cybersecurity vendor; they need a reliable ally who can support them through 
                uncertainty and proactively prevent security incidents.
              </p>
              <p className="whyChooseText">
                At Virtual IP, security isn't just a service it's our guiding principle. We embed 
                security into everything we do, ensuring that our partners can thrive in the face of 
                evolving threats. With world-class cybersecurity services and solutions, we empower 
                businesses to stay ahead of the curve and protect what matters most.
              </p>
            </div>
            <div className="whyChooseVisual">
              <div className="securityVisualization">
                <img src="homepage6.png" alt="Security Visualization" className="securityImage"/>
              </div>
            </div>
          </div>
        </section>

        {/* Our Services Section */}
        <section className="servicesSection">
          <h2 className="sectionTitle">OUR SERVICES</h2>
          <div className="servicesGrid">
            <div className="serviceCard" onClick={() => window.location.href = '/services/cyber-services'}>
              <div className="serviceIcon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <circle cx="12" cy="16" r="1"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <h3 className="serviceTitle">Cybersecurity Services</h3>
              <p className="serviceDescription">
                Comprehensive security solutions to protect your business from cyber threats, including penetration testing, vulnerability assessments, and compliance support.
              </p>
              <a href="/services/cyber-services" className="serviceLink">
                Learn More
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
            </div>
            
            <div className="serviceCard" onClick={() => window.location.href = '/ai-services'}>
              <div className="serviceIcon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 1v6m0 6v6"/>
                  <path d="m21 12-6-3v6l6-3z"/>
                  <path d="m3 12 6-3v6l-6-3z"/>
                  <path d="m12 1 3 6-3 6-3-6 3-6z"/>
                  <path d="m12 23-3-6 3-6 3 6-3 6z"/>
                </svg>
              </div>
              <h3 className="serviceTitle">Artificial Intelligence Services</h3>
              <p className="serviceDescription">
                Advanced AI solutions and machine learning implementations to automate processes, enhance decision-making, and drive digital transformation.
              </p>
              <a href="/ai-services" className="serviceLink">
                Learn More
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
            </div>
             <div className="serviceCard" onClick={() => window.location.href = '/web-services'}>
      <div className="serviceIcon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="4" width="20" height="16" rx="2" ry="2"/>
          <path d="M2 8h20"/>
          <path d="M6 16h.01"/>
          <path d="M10 16h.01"/>
        </svg>
      </div>
      <h3 className="serviceTitle">Web Development Services</h3>
      <p className="serviceDescription">
        Custom web applications built using the MERN stack and Next.js, including admin dashboards, real-time apps, and secure, scalable full-stack solutions.
      </p>
      <a href="/web-services" className="serviceLink">
        Learn More
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </a>
    </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;